import { useMemo } from 'react';
import { Router, type Navigator } from 'react-router-dom';

/** Opens an in-app path in a new tab: the full staff app, with its own sign-in. */
export type OpenInNewTab = (path: string) => void;

const openInBrowserTab: OpenInNewTab = (path) => {
  window.open(path, '_blank', 'noopener');
};

/** A react-router destination as a path string. */
function pathOf(to: Parameters<Navigator['push']>[0]): string {
  if (typeof to === 'string') return to;
  return `${to.pathname ?? ''}${to.search ?? ''}${to.hash ?? ''}`;
}

export interface NewTabRouterProps {
  /** The path the tree believes it is on (screen context reads it). */
  readonly path: string;
  readonly children: React.ReactNode;
  /** Injected in tests. */
  readonly openInNewTab?: OpenInNewTab;
}

/**
 * A router that never moves: the tree under it always sits at `path`, and every
 * navigation (a `<Link>`, `useNavigate`) opens that destination in a new tab
 * instead. For the assistant panel inside the Salesforce-embedded editor, where
 * the frame must stay on the proposal: an event or person the assistant links
 * to opens in BCE Cloud beside it. Same origin, so `<Link>` hrefs stay real for
 * middle-click and "copy link".
 */
export function NewTabRouter({ path, children, openInNewTab = openInBrowserTab }: NewTabRouterProps): React.ReactElement {
  const navigator = useMemo<Navigator>(
    () => ({
      createHref: (to) => pathOf(to),
      push: (to) => openInNewTab(pathOf(to)),
      replace: (to) => openInNewTab(pathOf(to)),
      go: () => {
        /* no history inside the frame */
      },
    }),
    [openInNewTab],
  );
  return (
    <Router location={path} navigator={navigator}>
      {children}
    </Router>
  );
}
